import React from "react";
import "./Card.scss";

export type CardProps = Omit<React.HTMLAttributes<HTMLElement>, "title"> & {
  /** A header row: the title in the label font */
  title?: React.ReactNode;
  /** A line under the title */
  subtitle?: React.ReactNode;
  /** Buttons at the right of the header */
  actions?: React.ReactNode;
  /** An icon before the title */
  icon?: React.ReactNode;
  /** Body padding: none (tables, lists edge to edge) · sm 12px · md 20px (default) */
  padding?: "none" | "sm" | "md";
  /** A footer row under the body */
  footer?: React.ReactNode;
  /** The slanted cut top-right corner of the brand's feature cards */
  cut?: boolean;
};

/** A white card on the grey page: a section of a page, a panel, a list. Flat, a 1px line, no shadow. */
export function Card({ title, subtitle, actions, icon, padding = "md", footer, cut = false, className, children, ...rest }: CardProps): React.JSX.Element {
  const hasHead = title || subtitle || actions;
  return (
    <section {...rest} className={["ui-card", cut ? "ui-card--cut" : "", className].filter(Boolean).join(" ")}>
      {hasHead ? (
        <header className="ui-card__head">
          {icon ? <span className="ui-card__icon">{icon}</span> : null}
          <div className="ui-card__titles">
            {title ? <h3 className="ui-card__title">{title}</h3> : null}
            {subtitle ? <p className="ui-card__subtitle">{subtitle}</p> : null}
          </div>
          {actions ? <div className="ui-card__actions">{actions}</div> : null}
        </header>
      ) : null}
      <div className={`ui-card__body ui-card__body--${padding}`}>{children}</div>
      {footer ? <footer className="ui-card__foot">{footer}</footer> : null}
    </section>
  );
}

export default Card;
